import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Warning, X } from "@phosphor-icons/react";

/** Polls /api/webhooks/alerts every 30s and pulses when Resend reports a bounce or
 * complaint. Clicking the badge opens a popover with the unseen alerts and a
 * "Mark all seen" action so deliverability problems don't hide. */
export default function BounceAlertsBadge() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const seenRef = useRef(new Set());   // known alert email_ids so we can toast only on new arrivals

  const load = async () => {
    try {
      const { data } = await api.get("/webhooks/alerts");
      setAlerts(Array.isArray(data) ? data : []);
      // Toast once per newly-appearing alert (not on the initial mount)
      if (seenRef.current.size > 0) {
        (data || []).forEach((a) => {
          const key = `${a.email_id}:${a.recipient}:${a.created_at}`;
          if (!seenRef.current.has(key)) {
            seenRef.current.add(key);
            toast.error(`Deliverability alert: ${a.kind.replace("email.", "")} for ${a.recipient}`, {
              description: `email_id ${String(a.email_id || "").slice(0, 8)}…`,
              duration: 8000,
            });
          }
        });
      } else {
        // First load — seed the set so we don't spam-toast historical alerts.
        (data || []).forEach((a) => seenRef.current.add(`${a.email_id}:${a.recipient}:${a.created_at}`));
      }
    } catch (err) {
      // Poll endpoint transient failure — badge stays at last known count, retries next tick.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[bounce-alerts] poll failed", err);
      }
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const markAllSeen = async () => {
    if (!alerts.length) return;
    setBusy(true);
    try {
      await api.post("/webhooks/alerts/mark-seen", {});
      setAlerts([]);
      seenRef.current.clear();
      setOpen(false);
      toast.success("Bounce alerts cleared");
    } catch {
      toast.error("Could not clear alerts");
    } finally {
      setBusy(false);
    }
  };

  const count = alerts.length;

  return (
    <div className="relative">
      <button
        data-testid="bounce-alerts-badge"
        onClick={() => setOpen((v) => !v)}
        title={count ? `${count} unresolved delivery ${count === 1 ? "alert" : "alerts"}` : "No delivery alerts"}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 border transition-colors text-xs ${
          count
            ? "border-destructive/70 text-destructive hover:bg-destructive/10 animate-pulse"
            : "border-border text-muted-foreground hover:text-primary hover:border-primary/40"
        }`}
      >
        <span className="flex items-center gap-2 font-mono uppercase tracking-widest">
          <Warning size={12} weight={count ? "fill" : "regular"} />
          <span>Deliverability</span>
        </span>
        {count > 0 && (
          <span data-testid="bounce-alerts-count" className="font-mono font-bold text-[0.6875rem] bg-destructive/20 border border-destructive/60 px-1.5 py-0.5">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="bounce-alerts-popover"
          className="absolute bottom-full left-0 right-0 mb-2 bg-card border border-border shadow-lg max-h-72 overflow-y-auto"
        >
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
              Resend alerts ({count})
            </div>
            <button
              data-testid="bounce-alerts-close"
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={12} />
            </button>
          </div>
          {count === 0 ? (
            <div className="p-4 text-xs text-muted-foreground text-center">
              All email is landing cleanly. Bounces and spam complaints will show up here.
            </div>
          ) : (
            <>
              <ul className="divide-y divide-border">
                {alerts.map((a, i) => (
                  <li key={i} className="px-3 py-2 text-xs" data-testid={`bounce-alert-${i}`}>
                    <div className="flex items-center gap-2 text-destructive font-mono uppercase tracking-widest text-[0.625rem]">
                      <Warning size={10} weight="fill" />
                      {a.kind?.replace("email.", "") || "bounce"}
                    </div>
                    <div className="text-foreground truncate mt-0.5" title={a.recipient}>{a.recipient}</div>
                    <div className="text-[0.625rem] text-muted-foreground font-mono truncate">
                      {String(a.created_at || "").slice(0, 16).replace("T", " ")} · id {String(a.email_id || "").slice(0, 8) || "—"}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="p-2 border-t border-border">
                <button
                  data-testid="bounce-alerts-mark-seen"
                  onClick={markAllSeen}
                  disabled={busy}
                  className="w-full text-[0.625rem] font-mono uppercase tracking-widest text-primary hover:bg-primary/10 py-1.5 disabled:opacity-50"
                >
                  {busy ? "Clearing…" : "Mark all seen"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
